import { useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Menu, X } from "lucide-react";
import { useState } from "react";
import { Logo } from "@/components/chrome";
import { SITE } from "@/data/site";
import { SERVICES_FR } from "@/data/servicesFr";
import { findProvince, getMeta } from "@/lib/seo";

const QC = findProvince("quebec");

function SeoManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const m = getMeta(pathname, window.location.origin);
    document.title = m.title;
    document.documentElement.lang = pathname.startsWith("/fr") ? "fr-CA" : "en-CA";
    const set = (sel, attr, val) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement(sel.startsWith("link") ? "link" : "meta");
        if (sel === 'meta[name="description"]') el.setAttribute("name", "description");
        if (sel === 'meta[name="keywords"]') el.setAttribute("name", "keywords");
        if (sel === 'link[rel="canonical"]') el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
      }
      el.setAttribute(attr, val);
    };
    set('meta[name="description"]', "content", m.description);
    set('meta[name="keywords"]', "content", m.keywords);
    set('link[rel="canonical"]', "href", window.location.origin + pathname);
  }, [pathname]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

const NAV_FR = [
  { label: "Accueil", to: "/fr" },
  { label: "Services", to: "/fr/services" },
  { label: "Villes", to: "/fr#villes" },
  { label: "English", to: "/" },
];

function HeaderFr() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl" data-testid="header-fr">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
        <Link to="/fr" data-testid="logo-fr-link" aria-label="Tbay Cleaning Services — Accueil"><Logo /></Link>
        <nav className="hidden items-center gap-8 lg:flex" data-testid="main-nav-fr">
          {NAV_FR.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-fr-${n.label.toLowerCase()}-link`}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-900">{n.label}</Link>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <a href={SITE.emailHref} data-testid="header-fr-email-button" aria-label={`Écrire à ${SITE.email}`}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-cyan-600 hover:text-cyan-700">
            <Mail className="h-4 w-4" />
          </a>
          <a href={SITE.phoneHref} data-testid="header-fr-call-button"
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-cyan-700">
            <Phone className="h-4 w-4" /> {SITE.phone}
          </a>
        </div>
        <button type="button" data-testid="mobile-menu-fr-button" aria-label="Ouvrir le menu" onClick={() => setOpen(!open)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-700 lg:hidden">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-200 bg-white px-5 py-6 lg:hidden" data-testid="mobile-menu-fr">
          <nav className="flex flex-col gap-4">
            {NAV_FR.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-base font-medium text-slate-700">{n.label}</Link>
            ))}
          </nav>
          <a href={SITE.phoneHref} data-testid="mobile-fr-call-button"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white">
            <Phone className="h-4 w-4" /> Appelez le {SITE.phone}
          </a>
        </div>
      )}
    </header>
  );
}

function FooterFr() {
  return (
    <footer className="bg-slate-900 text-slate-400" data-testid="footer-fr">
      <div className="mx-auto max-w-7xl px-5 py-16">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo dark />
            <p className="mt-5 max-w-sm text-sm leading-relaxed">
              Le réseau canadien de nettoyage résidentiel et commercial. Équipes locales assurées et vérifiées,
              partout au Québec et au Canada.
            </p>
            <div className="mt-6 space-y-3">
              <a href={SITE.phoneHref} data-testid="footer-fr-call-link" className="flex items-center gap-3 text-white transition-colors hover:text-cyan-300">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-500/15 text-cyan-300"><Phone className="h-4 w-4" /></span>
                <span className="font-semibold">{SITE.phone}</span>
              </a>
              <a href={SITE.emailHref} data-testid="footer-fr-email-link" className="flex items-center gap-3 text-white transition-colors hover:text-cyan-300">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-500/15 text-cyan-300"><Mail className="h-4 w-4" /></span>
                <span className="font-semibold">{SITE.email}</span>
              </a>
              <p className="flex items-center gap-3 text-sm">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-slate-300"><MapPin className="h-4 w-4" /></span>
                Entreprise 100 % canadienne — service partout au Québec
              </p>
            </div>
          </div>
          <div className="lg:col-span-3">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Services</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              {SERVICES_FR.map((s) => (
                <li key={s.slug}>
                  <Link to={`/fr/services/${s.slug}`} data-testid={`footer-fr-service-${s.slug}`} className="transition-colors hover:text-white">{s.name}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Villes desservies au Québec</p>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2.5 text-sm">
              {QC.cities.map((c) => (
                <Link key={c.slug} to={`/fr/villes/${c.slug}`} data-testid={`footer-fr-city-${c.slug}`} className="transition-colors hover:text-white">
                  Nettoyage à {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-12 flex flex-col gap-3 border-t border-white/10 pt-6 text-xs sm:flex-row sm:items-center sm:justify-between">
          <p>© 2026 {SITE.legalName} Tous droits réservés.</p>
          <Link to="/" data-testid="footer-fr-en-link" className="transition-colors hover:text-white">English version →</Link>
        </div>
      </div>
    </footer>
  );
}

export default function LayoutFr() {
  return (
    <div className="min-h-screen bg-[#FAFAFC] font-sans text-slate-600 antialiased" lang="fr-CA">
      <SeoManager />
      <ScrollToTop />
      <HeaderFr />
      <main className="pb-24"><Outlet /></main>
      <FooterFr />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/90 backdrop-blur-xl sm:border-0 sm:bg-transparent sm:backdrop-blur-none" data-testid="sticky-mobile-cta-fr">
        <div className="pointer-events-auto grid grid-cols-2 gap-2 p-3 sm:mx-auto sm:mb-5 sm:flex sm:w-max sm:items-center sm:gap-2 sm:rounded-full sm:border sm:border-slate-200 sm:bg-white/90 sm:p-2 sm:shadow-[0_16px_50px_-12px_rgba(15,23,42,0.4)] sm:backdrop-blur-xl">
          <a href={SITE.phoneHref} data-testid="sticky-fr-call-button"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-cyan-700 sm:px-6">
            <Phone className="h-4 w-4" />
            <span className="sm:hidden">Appeler</span>
            <span className="hidden sm:inline">Appeler le {SITE.phone}</span>
          </a>
          <a href={SITE.emailHref} data-testid="sticky-fr-email-button"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-cyan-600 hover:text-cyan-700 sm:px-6">
            <Mail className="h-4 w-4" />
            <span className="sm:hidden">Courriel</span>
            <span className="hidden sm:inline">{SITE.email}</span>
          </a>
        </div>
      </div>
    </div>
  );
}
